// @flow
import * as React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { APIContext, Confirmation, withConfirmation } from 'speed-js-react';
import { QuickViewContext } from 'esm-web-pro/containers/tables/columns/QuickViewComponent';
import { useDispatch } from 'react-redux';
import { closePanel, openPanel } from 'esm-web-pro/redux/side-panel';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import EditIcon from 'esm-web-pro/components/layout/sidePanel/icons/EditIcon';
import DeleteIcon from 'esm-web-pro/components/layout/sidePanel/icons/DeleteIcon';
import MainActions from 'esm-web-pro/components/layout/sidePanel/MainActions';
import Editor from 'esm-web-pro/components/pages/projectManagement/dashboard/Editor';
import { useHistory } from 'react-router';

const NOMENCLATURE_SIDEPANEL_KEY = 'NOMENCLATURE_SIDEPANEL_KEY';

const messages = defineMessages({
    deleteNode: {
        id: 'delete-nomenclature-type-confirm',
        defaultMessage: 'Are you sure you want to delete the nomenclature type?',
    },
    actionFailed: {
        id: 'response-error',
        defaultMessage: 'Error occurred, please try again later.',
    },
});

type Props = {
    nomenclatureType: Object,
    reload: Function,
    confirm: (confirmation: Confirmation) => void,
    panelId: string,
}

const NomenclatureTypeActions = (props: Props) => {
    const {
        nomenclatureType, reload, confirm, panelId,
    } = props;

    const history = useHistory();

    const isQuickview = React.useContext(QuickViewContext);

    const dispatch = useDispatch();
    const intl = useIntl();

    const { apiClient } = React.useContext(APIContext);

    const confirmFailed = React.useCallback((message) => confirm({
        message,
        actions: {
            cancel: {
                label: <FormattedMessage id="cancel-delete-btn" defaultMessage="Cancel" />,
                callback: () => {
                },
                type: 'negative',
            },
        },
    }), [confirm]);

    const invokeConfirmDialog = React.useCallback((message, confirmCallback) => confirm({
        message,
        actions: {
            cancel: {
                label: <FormattedMessage id="cancel-btn" defaultMessage="Cancel" />,
                callback: () => {
                },
                type: 'negative',
            },
            confirm: {
                label: <FormattedMessage id="confirm-btn" defaultMessage="Confirm" />,
                callback: confirmCallback,
                type: 'positive',
            },
        },
    }), [confirm]);

    const deleteNomenclatureCallback = () => {
        apiClient.resources.nomenclatureType.delete(nomenclatureType.id)
            .then(() => {
                if (isQuickview) {
                    dispatch(closePanel(panelId));
                    reload(false);
                } else if (!isQuickview) {
                    history.push('/administration/nomenclatures/types');
                }
            }).catch((res) => {
                if (res.data && res.data.detail) {
                    confirmFailed(res.data.detail);
                } else {
                    confirmFailed(intl.formatMessage(messages.actionFailed));
                }
            });
    };

    const nomenclatureEditorTitle = (
        <FormattedMessage
            id="nomenclature-edit-nomenclature-type"
            defaultMessage="Edit nomenclature"
        />
    );

    const nomenclatureActions = [
        {
            key: `${nomenclatureType.id}-edit`,
            icon: <EditIcon size="small" />,
            callback: () => dispatch(openPanel(NOMENCLATURE_SIDEPANEL_KEY, '', null, true)),
            tooltip: intl.formatMessage(actionMessages.edit),
            marginVertical: '15px',
            radius: '0%',
            color: 'transparent',
        },
        {
            key: `${nomenclatureType.id}-delete`,
            icon: <DeleteIcon size="small" />,
            callback: () => invokeConfirmDialog(
                intl.formatMessage(messages.deleteNode), deleteNomenclatureCallback,
            ),
            tooltip: intl.formatMessage(actionMessages.delete),
            marginVertical: '15px',
            radius: '0%',
            color: 'transparent',
        },
    ];

    return (
        <>
            <Editor
                id={NOMENCLATURE_SIDEPANEL_KEY}
                object={nomenclatureType}
                resourceName="nomenclatureType"
                idAccessor="id"
                editorTitle={nomenclatureEditorTitle}
                onSave={reload}
            />
            <MainActions main actions={nomenclatureActions} />
        </>
    );
};

export default withConfirmation(NomenclatureTypeActions);
